export type AddressValue = {
  address?: string;
  streetNumber?: string;
  streetName?: string;
  aptNumber?: string;
  city?: string;
  state?: string;
  zipCode?: string;
};

export const ADDRESS_PARTS: { key: keyof AddressValue; label: string }[] = [
  { key: 'streetNumber', label: 'Street Number' },
  { key: 'streetName', label: 'Street Name' },
  { key: 'aptNumber', label: 'Apt / Unit' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'zipCode', label: 'Zip Code' },
];

export const REQUIRED_ADDRESS_PARTS: (keyof AddressValue)[] = ['streetNumber', 'streetName', 'city', 'state', 'zipCode'];

export const formatAddressLine = (value: AddressValue): string => {
  const street = [value?.streetNumber, value?.streetName, value?.aptNumber]
    .filter(Boolean)
    .join(' ');
  return [street, value?.city, value?.state, value?.zipCode]
    .filter(part => part && String(part).trim())
    .join(', ');
};

export type IdentityValues = Record<string, any>;

export const DRIVER_LICENSE_ID_TYPE = "US Driver's License / Government ID";
export const US_CITIZEN = 'US Citizen';

const isUsCitizenId = (idType: any, issuingCountry: any) =>
  idType === DRIVER_LICENSE_ID_TYPE || String(issuingCountry ?? '').trim().toUpperCase() === 'USA';

const FINANCIAL_FIELDS = ['assets', 'liabilities'];
const PORTAL_ONLY_ROWS = ['assets', 'liabilities', 'netWorth'];

export const toAmount = (value: any): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : null;
};

export const withoutPortalOnlyRows = <T extends { itemName?: string }>(rows: T[]): T[] =>
  rows.filter(row => !PORTAL_ONLY_ROWS.includes(row?.itemName));
const PERSONAL_FIELDS = ['dateOfBirth', 'address', 'idType', 'issuingState', 'idNumber', 'citizenshipStatus', 'expiryDate'];

const initialAddress = (data: Record<string, any>): AddressValue => {
  if (data.addressDetails) {
    return { ...data.addressDetails, address: data.address };
  }
  if (data.portalAddressDetails) {
    return { ...data.portalAddressDetails, address: formatAddressLine(data.portalAddressDetails) };
  }
  return { address: data.address };
};

const isEmpty = (value: any) => value === null || value === undefined || value === '';

export const serverValue = (data: Record<string, any>, itemName: string) => {
  const row = (data.extractedData ?? []).find(row => row?.itemName === itemName);
  return isEmpty(row?.data) ? row?.expectedData : row.data;
};

export const buildInitialValues = (data: Record<string, any>): IdentityValues => {
  const values: IdentityValues = {};
  PERSONAL_FIELDS.forEach(fieldName => {
    const value = serverValue(data, fieldName);
    values[fieldName] = isEmpty(value) ? data[fieldName] : value;
  });
  const issuingCountry = serverValue(data, 'issuingCountry');
  values.citizenshipStatus = isUsCitizenId(values.idType, issuingCountry) ? US_CITIZEN : issuingCountry || '';
  values.address = initialAddress(data);
  FINANCIAL_FIELDS.forEach(fieldName => (values[fieldName] = serverValue(data, fieldName)));
  return values;
};

export const buildSaveValues = (values: IdentityValues, initial: IdentityValues): Record<string, any> => {
  const saved: Record<string, any> = {};
  PERSONAL_FIELDS.forEach(fieldName => (saved[fieldName] = values[fieldName] ?? null));
  const { address: addressLine, ...addressDetails } = (values.address ?? {}) as AddressValue;
  saved.address = addressLine ?? formatAddressLine(addressDetails);
  saved.addressDetails = addressDetails;
  FINANCIAL_FIELDS
    .filter(fieldName => toAmount(values[fieldName]) !== toAmount(initial[fieldName]))
    .forEach(fieldName => (saved[fieldName] = toAmount(values[fieldName])));
  return saved;
};
